import { Link } from "@tanstack/react-router";
import { formatearMoneda, type EstadoCotizacion } from "@uirtus/shared";
import { useMemo, useState } from "react";
import { Aviso, Entrada, Etiqueta, Pestanas, type Pestana } from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import { useCotizaciones } from "../lib/cotizaciones";
import { useDebounce } from "../lib/use-debounce";

const ESTADOS_ORDEN: EstadoCotizacion[] = [
  "BORRADOR",
  "ENVIADA",
  "ACEPTADA",
  "RECHAZADA",
  "VENCIDA",
  "CANCELADA",
];

const ETIQUETA_ESTADO: Record<EstadoCotizacion, string> = {
  BORRADOR: "Borrador",
  ENVIADA: "Enviada",
  ACEPTADA: "Aceptada",
  RECHAZADA: "Rechazada",
  VENCIDA: "Vencida",
  CANCELADA: "Cancelada",
};

const TONO_ESTADO: Record<EstadoCotizacion, "neutro" | "exito" | "alerta" | "peligro"> = {
  BORRADOR: "neutro",
  ENVIADA: "alerta",
  ACEPTADA: "exito",
  RECHAZADA: "peligro",
  VENCIDA: "peligro",
  CANCELADA: "neutro",
};

const formatearFecha = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" }).format(
    new Date(`${iso}T00:00:00`),
  );

type FiltroEstado = "TODAS" | EstadoCotizacion;

export function Cotizaciones() {
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState<FiltroEstado>("TODAS");
  const busqueda = useDebounce(texto, 300);

  const { data, isLoading, isError, error } = useCotizaciones({ limite: 200 });
  const lista = useMemo(() => data ?? [], [data]);

  // La categoría no se manda a la API: se piden todas y el reparto en
  // pestañas se hace aquí, igual que en servicios. Así los contadores de
  // todos los estados son reales y cambiar de pestaña no dispara consulta.
  const porEstado = useMemo(() => {
    const grupos = new Map<EstadoCotizacion, number>();
    for (const e of ESTADOS_ORDEN) grupos.set(e, 0);
    for (const c of lista) grupos.set(c.estado, (grupos.get(c.estado) ?? 0) + 1);
    return grupos;
  }, [lista]);

  const filtradas = useMemo(() => {
    let base = lista;
    if (estado !== "TODAS") base = base.filter((c) => c.estado === estado);
    const t = busqueda.trim().toLowerCase();
    if (t) {
      base = base.filter(
        (c) =>
          c.folio.toLowerCase().includes(t) ||
          c.titulo.toLowerCase().includes(t) ||
          c.cliente.razonSocial.toLowerCase().includes(t) ||
          c.cliente.rfc.toLowerCase().includes(t),
      );
    }
    return base;
  }, [lista, estado, busqueda]);

  const pestanas: Pestana<FiltroEstado>[] = [
    { clave: "TODAS", texto: "Todas", conteo: lista.length },
    ...ESTADOS_ORDEN.map((e) => ({
      clave: e as FiltroEstado,
      texto: ETIQUETA_ESTADO[e],
      conteo: porEstado.get(e) ?? 0,
    })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Cotizaciones</h1>
          <p className="text-sm text-slate-500">
            De aquí sale la factura cuando el cliente acepta.
          </p>
        </div>
        <Link
          to="/cotizaciones/nueva"
          className="rounded-md bg-marca-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-marca-700"
        >
          Nueva cotización
        </Link>
      </div>

      <Entrada
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Buscar por folio, título, RFC o razón social"
        className="w-full sm:w-96"
      />

      {isError && (
        <Aviso>
          {error instanceof ErrorApi ? error.message : "No se pudo cargar la lista"}
        </Aviso>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : isError ? null : (
        <div>
          <Pestanas pestanas={pestanas} activa={estado} alCambiar={setEstado} />

          <div className="pt-4">
            {filtradas.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 px-4 py-12 text-center dark:border-slate-700">
                <p className="text-sm text-slate-500">
                  {busqueda
                    ? `Ninguna cotización coincide con "${busqueda}".`
                    : estado === "TODAS"
                      ? "Todavía no hay cotizaciones."
                      : `No hay cotizaciones en ${ETIQUETA_ESTADO[estado].toLowerCase()}.`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
                    <tr>
                      <th className="px-4 py-2">Folio</th>
                      <th className="px-4 py-2">Cliente</th>
                      <th className="px-4 py-2">Título</th>
                      <th className="px-4 py-2">Estado</th>
                      <th className="px-4 py-2">Válida hasta</th>
                      <th className="px-4 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filtradas.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-2 font-mono text-xs">
                          {c.folio}
                          {c.version > 1 && (
                            <span className="ml-1 text-slate-400">v{c.version}</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div>{c.cliente.razonSocial}</div>
                          <div className="font-mono text-xs text-slate-500">
                            {c.cliente.rfc}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <Link
                            to="/cotizaciones/$id"
                            params={{ id: c.id }}
                            className="font-medium text-marca-600 hover:underline dark:text-marca-500"
                          >
                            {c.titulo}
                          </Link>
                        </td>
                        <td className="px-4 py-2">
                          <Etiqueta tono={TONO_ESTADO[c.estado]}>
                            {ETIQUETA_ESTADO[c.estado]}
                          </Etiqueta>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">
                          {formatearFecha(c.validaHasta)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {formatearMoneda(c.total, c.moneda)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
